import { SessionStatus } from '@tools/sessionUtils/SessionStatus';
import { EventInfo, WebcastPlayer, Maybe } from '@graphql/gql/graphql';
import { determineVendor, Vendor, isCventStudioEmbed, VendorType } from '@tools/sessionUtils/Vendor';
import getWebcastLink from '@tools/sessionUtils/getWebcastLink';
import {
  checkHasRecordingInProgress,
  checkIsBrightcoveStream,
  checkIsContentPresent,
  checkIsEmbeddedCollaboration,
  checkIsWebcastCVCCollaborative,
  checkIsVirtualRoomEmbedded,
  isOnDemandVideo,
  returnWebcastPlayer
} from '@components/sessionDetails/sessionDetailsUtils';
import { ParsedUrlQuery } from 'querystring';
import {
  AVATAR_SIZES,
  AVATAR_SLIDE_HEIGHT,
  AVATAR_SLIDE_WIDTH,
  SINGLE_SPEAKER_AVATAR_SIZES,
  SLIDER_DOTS_TOP_MARGIN,
  TWO_SPEAKERS_AVATAR_ONE_SIZES,
  TWO_SPEAKERS_AVATAR_TWO_SIZES
} from './landingConstants';

export const LOGIN_ERROR = 'login-error';

export const REGISTRATION_STATUS = {
  PENDING: 'Pending',
  ACTIVE: 'Active'
};

export const chooseDotsTopMargin = (breakpoints): number => {
  const { isMobile, isM } = breakpoints;

  if (isMobile) return SLIDER_DOTS_TOP_MARGIN.SMALL;
  if (isM) return SLIDER_DOTS_TOP_MARGIN.MEDIUM;

  return SLIDER_DOTS_TOP_MARGIN.LARGE;
};

export const chooseOneSpeakerAvatarSize = (sizeString: string): number => {
  if (sizeString === AVATAR_SIZES.SMALL) return SINGLE_SPEAKER_AVATAR_SIZES.SMALL;
  if (sizeString === AVATAR_SIZES.MEDIUM) return SINGLE_SPEAKER_AVATAR_SIZES.MEDIUM;

  return SINGLE_SPEAKER_AVATAR_SIZES.LARGE;
};

export const chooseTwoSpeakersAvatarOneSize = (sizeString: string): number => {
  if (sizeString === AVATAR_SIZES.SMALL) return TWO_SPEAKERS_AVATAR_ONE_SIZES.SMALL;
  if (sizeString === AVATAR_SIZES.MEDIUM) return TWO_SPEAKERS_AVATAR_ONE_SIZES.MEDIUM;

  return TWO_SPEAKERS_AVATAR_ONE_SIZES.LARGE;
};

export const chooseTwoSpeakersAvatarTwoSize = (sizeString: string): number => {
  if (sizeString === AVATAR_SIZES.SMALL) return TWO_SPEAKERS_AVATAR_TWO_SIZES.SMALL;
  if (sizeString === AVATAR_SIZES.MEDIUM) return TWO_SPEAKERS_AVATAR_TWO_SIZES.MEDIUM;

  return TWO_SPEAKERS_AVATAR_TWO_SIZES.LARGE;
};

export const chooseAvatarCarouselSlideHeight = (sizeString: string): number => {
  if (sizeString === AVATAR_SIZES.SMALL || sizeString === AVATAR_SIZES.MEDIUM) return AVATAR_SLIDE_HEIGHT.MEDIUM;

  return AVATAR_SLIDE_HEIGHT.LARGE;
};

export const chooseAvatarCarouselWidth = (sizeString: string): number => {
  if (sizeString === AVATAR_SIZES.SMALL) {
    return AVATAR_SLIDE_WIDTH.SMALL;
  }
  if (sizeString === AVATAR_SIZES.MEDIUM) return AVATAR_SLIDE_WIDTH.MEDIUM;

  return AVATAR_SLIDE_WIDTH.LARGE;
};

export const chooseSpeakerCardSize = (breakpoints): string => {
  const { widthWindow } = breakpoints;

  if (widthWindow <= 450) return AVATAR_SIZES.SMALL;
  if (widthWindow > 450 && widthWindow <= 600) return AVATAR_SIZES.MEDIUM;
  if (widthWindow > 600 && widthWindow < 800) return AVATAR_SIZES.LARGE;
  if (widthWindow >= 800 && widthWindow < 1300) return AVATAR_SIZES.SMALL;
  if (widthWindow >= 1300 && widthWindow < 1450) return AVATAR_SIZES.MEDIUM;
  if (widthWindow >= 1450 && widthWindow < 1750) return AVATAR_SIZES.SMALL;
  if (widthWindow >= 1750 && widthWindow < 2100) return AVATAR_SIZES.MEDIUM;

  return AVATAR_SIZES.LARGE;
};

export const chooseSpeakerAvatarSize = (breakpoints): string => {
  const { isM, isS, isMobile } = breakpoints;
  if (isM) return AVATAR_SIZES.MEDIUM;
  if (isS || isMobile) return AVATAR_SIZES.SMALL;

  return AVATAR_SIZES.LARGE;
};

export const extractJoinSessionActionProps = ({
  isLive,
  isZoomEmbedEnabled = false, // TODO WAX-2383 remove isZoomEnabled post-release
  sessionStatus,
  statusOnLoad,
  virtual,
  webcast
}): {
  isContentPresent: boolean;
  isVirtualRoomEmbedded: boolean;
  onDemandVideo: boolean;
  streamStatus: string;
  vendor: VendorType;
  webcastFormat: string;
  webcastVideoId: string;
} => {
  const webcastFormat = webcast?.format;
  const links = webcast?.links;
  const solutionType = webcast?.solutionType;
  const webcastPlayer: Partial<WebcastPlayer> = returnWebcastPlayer(webcast);
  const streamRecordingStatus = webcastPlayer?.stream?.recording?.status;
  const webcastVideoId = webcastPlayer?.videoId;

  const streamStatus = webcastPlayer?.stream?.status;
  const vendor = determineVendor(webcast, sessionStatus, isZoomEmbedEnabled);
  const onDemandVideo = isOnDemandVideo(sessionStatus, webcast);
  const isVirtualRoomEmbedded = checkIsVirtualRoomEmbedded({
    isVirtual: virtual,
    sessionStatus,
    vendor,
    isZoomEmbedEnabled
  });
  const webcastLink = getWebcastLink(
    isVirtualRoomEmbedded ? statusOnLoad : sessionStatus,
    links,
    vendor,
    webcastFormat
  );

  const contentUrl = vendor === Vendor.CVENT_VOD ? webcastPlayer?.videoUrl : webcastLink?.href;

  const isCVCCollaborative = checkIsWebcastCVCCollaborative(webcastFormat, webcast?.type, solutionType);

  const hasRecordingInProgress = checkHasRecordingInProgress({
    hasOnDemandVideo: onDemandVideo,
    vendor,
    webcastVideoId,
    streamRecordingStatus
  });

  const isContentPresent = checkIsContentPresent({
    isStudio: isCventStudioEmbed(solutionType),
    isEmbeddedCollab: checkIsEmbeddedCollaboration(vendor),
    statusOnLoad,
    videoContent: checkIsBrightcoveStream(vendor) ? webcastVideoId : contentUrl,
    hasOnDemandVideo: onDemandVideo,
    isLive,
    isCVCCollaborative,
    joinEmbedRole: null,
    hasRecordingInProgress
  });

  return {
    isContentPresent,
    isVirtualRoomEmbedded,
    onDemandVideo,
    streamStatus,
    vendor,
    webcastFormat,
    webcastVideoId
  };
};

export const hasDescriptionText = (eventInfo: Pick<EventInfo, 'description'>): boolean =>
  eventInfo?.description && eventInfo?.description.length > 0;

export const hasSessionStartedOrEnded = (sessionStatus): boolean =>
  sessionStatus === SessionStatus.LESS_THAN_FIVE_MIN_BEFORE ||
  sessionStatus === SessionStatus.DURING ||
  sessionStatus === SessionStatus.LESS_THAN_ONE_HOUR_AFTER ||
  sessionStatus === SessionStatus.MORE_THAN_ONE_HOUR_AFTER ||
  sessionStatus === SessionStatus.AFTER;

export const hasSpeakers = (speakers): boolean => Array.isArray(speakers) && speakers?.length > 0;

export const shouldAutoPlay = ({
  isGlobalModerator,
  sessionsToModerate,
  sessionId
}: {
  isGlobalModerator: boolean;
  sessionsToModerate: string[];
  sessionId: string;
}): boolean => {
  return !(isGlobalModerator || sessionsToModerate?.includes(sessionId));
};

export const hasOnDemandContentAvailable = ({
  onDemandVideo,
  sessionStatus,
  isEnrolledOrIncluded,
  isContentPresent,
  isVirtualRoomEmbedded
}: {
  onDemandVideo: boolean;
  sessionStatus: string;
  isEnrolledOrIncluded: boolean;
  isContentPresent: boolean;
  isVirtualRoomEmbedded: boolean;
}): boolean =>
  onDemandVideo &&
  sessionStatus === SessionStatus.AFTER &&
  isEnrolledOrIncluded &&
  isContentPresent &&
  isVirtualRoomEmbedded;

export const hasCollaborativeContentAvailable = ({
  sessionStatus,
  isEnrolledOrIncluded,
  isContentPresent
}: {
  sessionStatus: string;
  isEnrolledOrIncluded: boolean;
  isContentPresent: boolean;
}): boolean => sessionStatus === SessionStatus.DURING && isEnrolledOrIncluded && isContentPresent;

export function getRegistrationUrl(
  baseUrl: string,
  eventId: string,
  environment: string,
  queryParams: ParsedUrlQuery,
  isWebinarEmbed: boolean,
  reflexEmbeddedRegUrl?: string
): string {
  const searchParams = new URLSearchParams();
  const utmPrefix = 'utm_';

  let registrationUrl = `${baseUrl}/embedded-registration/${eventId}/register`;

  if (reflexEmbeddedRegUrl) {
    registrationUrl = `${reflexEmbeddedRegUrl}/webinar-embedded-registration/${eventId}/register`;
  } else {
    searchParams.append('hideHeaderFooter', 'true');
    if (isWebinarEmbed) {
      searchParams.append('isWebinarEmbed', 'true');
    }
  }

  if (environment) {
    searchParams.append('environment', environment);
  }
  if (queryParams) {
    Object.entries(queryParams).forEach(([queryParamsKey, queryParamsValue]) => {
      if (queryParamsKey === 'c' || queryParamsKey === 'originSourceType' || queryParamsKey.startsWith(utmPrefix)) {
        searchParams.append(queryParamsKey, queryParamsValue.toString());
      }
    });
  }

  registrationUrl += `?${searchParams.toString()}`;
  return registrationUrl;
}

export const chooseWebinarEndedText = ({
  now = new Date(),
  endTime
}: {
  now?: Date;
  endTime: string;
}): Maybe<(string | Record<string, number>)[]> => {
  if (!endTime) {
    return null;
  }

  const endDate = new Date(endTime);

  const minutesDifference = Math.floor(Math.abs(endDate.getTime() - now.getTime()) / (1000 * 60));

  if (minutesDifference >= 0 && minutesDifference < 60) {
    return ['Landing-Page-Main-Section-Sub-Header-Webinar-Ended-Minutes', { minutes: minutesDifference }];
  }

  const hoursDifference = Math.floor(Math.abs(Number(endDate) - Number(now)) / (1000 * 60 * 60));

  if (hoursDifference >= 1 && hoursDifference < 24) {
    return ['Landing-Page-Main-Section-Sub-Header-Webinar-Ended-Hours', { hours: hoursDifference }];
  }

  const daysDifference = Math.floor(Math.abs((Number(endDate) - Number(now)) / (1000 * 60 * 60 * 24)));

  return ['Landing-Page-Main-Section-Sub-Header-Webinar-Ended-Days', { days: daysDifference }];
};
